import { createSelector } from 'reselect';
import { authUserSelector } from 'selectors/authSelectors';
import { getUsersWithClients } from 'selectors/entities/userSelectors';
import Immutable from 'immutable';
import { addSuffixToWorkflowId } from 'components/task/TasksBoard/taskBoardHelper';
import { createImmutableSelector } from 'util/reselectUtils';

export const getTasksFromState = (state) => state.getIn(['entities', 'task', 'tasks']);

// Returns tasks
export const getTasks = createImmutableSelector(
  [getTasksFromState, (state) => state.getIn(['entities', 'project', 'projects']), (state, props) => props?.projectId],
  (tasks, projects, projectId) => {
    if (projectId) {
      tasks = tasks.filter((task) => task.get('projectId') === parseInt(projectId));
    }

    return tasks.map((task) => task.set('project', projects.get(task.get('projectId'))));
  },
);

export const getUserTasksIds = (state) => state.getIn(['entities', 'task', 'userTasksIds']);

// Returns task by id
export const getTaskById = (state, { taskId }) => state.getIn(['entities', 'task', 'tasks', taskId]);

// Returns parent task by child taskId
export const getParentTaskById = (state, { taskId }) => {
  const task = getTaskById(state, { taskId });
  if (!task) {
    return null;
  }

  const parent = getTaskById(state, { taskId: task.get('parentId') });
  return parent;
};

// Returns task with parent by id
export const getTaskByIdWithParent = createImmutableSelector([getTaskById, getParentTaskById], (task, parent) => {
  if (!task) {
    return null;
  }

  return task.set('parent', parent);
});

// Returns task statuses
const getTaskStatusesFromState = (state) => state.getIn(['entities', 'task', 'taskStatuses']);

// Скрываем старые дефолтные статусы
export const getTaskStatuses = createSelector(getTaskStatusesFromState, (taskStatuses) =>
  taskStatuses.filter((status) => !!status.get('workflowId')),
);

export const getProjectsByWorkflowId = createSelector(
  [(state) => state.getIn(['entities', 'project', 'projects']), (_, workflowId) => workflowId],
  (workflowProjects, workflowId) => workflowProjects.filter((project) => project.get('workflowId') === workflowId),
);

// Returns all task statuses, except FINISHED, to use them in board view
export const getBoardTaskStatuses = createImmutableSelector([getTaskStatuses], (taskStatuses) =>
  taskStatuses.filter((status) => status.get('alias') !== 'finished'),
);

// Returns task labels
export const getTaskLabels = (state) => state.getIn(['entities', 'task', 'taskLabels']);

// Return task status by id
export const getTaskStatusById = (state, { statusId }) => state.getIn(['entities', 'task', 'taskStatuses', statusId]);

// Return task label by id
export const getTaskLabelById = (state, { labelId }) => state.getIn(['entities', 'task', 'taskLabels', labelId]);

// Return task label groups
export const getTaskLabelGroups = (state) => state.getIn(['entities', 'task', 'taskLabelGroups']);

// Return workflows
export const getWorkflows = (state) => state.getIn(['entities', 'task', 'workflows']);

export const getWorkflowById = (state, workflowId) => state.getIn(['entities', 'task', 'workflows', workflowId]);

export const getWorkflowsMapStatusIds = createSelector([getWorkflows, getTaskStatuses], (workflows, statuses) => {
  const result = {};
  workflows.forEach((workflow) => {
    const statusesByWorkflowId = statuses
      .valueSeq()
      .filter((status) => status.get('workflowId') === workflow.get('workflowId'))
      .sort((a, b) => a.get('position') - b.get('position'));
    const workflowIdWithSuffix = addSuffixToWorkflowId(workflow.get('workflowId'));
    result[workflowIdWithSuffix] = [...statusesByWorkflowId.valueSeq().map((status) => status.get('statusId'))];
  });

  return result;
});

export const getTaskStatusesByWorkflowId = createImmutableSelector(
  [getTaskStatuses, (_, workflowId) => workflowId],
  (taskStatuses, workflowId) =>
    taskStatuses
      .filter((taskStatus) => taskStatus.get('workflowId') === workflowId)
      .sort((a, b) => a.get('position') - b.get('position')),
);

// Если у пользователя, нет доступных воркфлоу, возвращаем null
// Если мы передали workflowId, возвращаем его если он есть
// Иначе возвращаем ему первый доступный
export const getWorkflowId = createSelector([getWorkflows, (_, props) => props.workflowId], (workflows, workflowId) => {
  const ids = workflows.keySeq();
  if (!ids.size) {
    return null;
  }

  if (ids.includes(workflowId)) {
    return workflowId;
  }

  return ids.first();
});

//Return checklists
export const getChecklists = (state) => state.getIn(['entities', 'task', 'checklists']);

//Return checklist by id
export const getChecklistById = (state, props) => state.getIn(['entities', 'task', 'checklists', props?.listId]);

//Return checklist items
export const getChecklistItems = (state) => state.getIn(['entities', 'task', 'checklistItems']);

//Return checklist item by id
export const getChecklistItemById = (state, props) =>
  state.getIn(['entities', 'task', 'checklistItems', props?.itemId]);

// Return task templates
export const getTaskTemplates = (state) => state.getIn(['entities', 'task', 'taskTemplates']);

// Return task template by id
export const getTaskTemplateById = (state, { templateId }) => {
  return state.getIn(['entities', 'task', 'taskTemplates', parseInt(templateId)]);
};

const getScheduleFromState = (state) => state.getIn(['entities', 'schedule']);

// Return task schedules
export const getTaskSchedules = createSelector(getScheduleFromState, (schedule) =>
  schedule.filter((schedule) => schedule.get('entity') === 'task'),
);

export const getTaskSchedulesWithSummary = createImmutableSelector(
  [getTaskSchedules, getTasks, getUsersWithClients],
  (schedules, tasks, users) => {
    return schedules.map((schedule) => {
      let preparedSchedule = schedule;
      if (schedule.getIn(['tasks', 'items'])) {
        preparedSchedule = preparedSchedule.setIn(
          ['tasks', 'items'],
          preparedSchedule.getIn(['tasks', 'items']).map((taskId) => tasks.get(taskId)),
        );
      }
      return preparedSchedule.set('creator', users.get(schedule.get('creatorId')));
    });
  },
);

// Return task schedule by Id
export const getTaskScheduleById = (state, { scheduleId }) =>
  state.getIn(['entities', 'schedule', parseInt(scheduleId)]);

export const prepareTaskWithSummary = (statuses, labels, usersWithClients, schedules) => (task) => {
  let taskStatus = null,
    taskLabels = [],
    user = null,
    creator = null,
    schedule = null;

  if (task.get('privacyUsers')) {
    task = task.set(
      'privacyUsers',
      task
        .get('privacyUsers')
        .map((id) => usersWithClients.get(id))
        .filter((user) => user),
    );
  }

  if (task.get('spectators')) {
    task = task.set(
      'spectators',
      task
        .get('spectators')
        .map((id) => usersWithClients.get(id))
        .filter((user) => !!user),
    );
  }

  if (task.get('statusId') && statuses?.size) {
    taskStatus = statuses.get(task.get('statusId'));
  }

  if (task.get('userId') && usersWithClients) {
    user = usersWithClients.get(task.get('userId'));
  }

  if (task.get('labels') && labels?.size) {
    taskLabels = task.get('labels').map((id) => id && labels.get(id));
  }

  if (task.get('creatorId') && usersWithClients) {
    creator = usersWithClients.get(task.get('creatorId'));
  }

  if (task.get('scheduleId') && schedules) {
    schedule = schedules.get(task.get('scheduleId'));
  }

  return task.merge(
    Immutable.fromJS({
      labels: taskLabels,
      taskStatus,
      user,
      creator,
      schedule,
    }),
  );
};

// Returns tasks with related models info
export const getTasksWithSummary = createImmutableSelector(
  [getTasks, getTaskStatuses, getTaskLabels, getUsersWithClients, getTaskSchedules],
  (tasks, statuses, labels, usersWithClients, schedules) => {
    return tasks.map(prepareTaskWithSummary(statuses, labels, usersWithClients, schedules));
  },
);

// Returns active tasks of the authorized user.
export const getAuthUserTasks = createImmutableSelector([getTasksWithSummary, authUserSelector], (tasks, authUser) => {
  return tasks
    .filter((task) => {
      const isCurrentUser =
        authUser &&
        (task.get('userId') === authUser.get('userId') || task.get('coPerformerId') === authUser.get('userId'));
      const isActive = task.get('status') === 'active';

      return isActive && isCurrentUser;
    })
    .map((task) =>
      task
        .set('user', authUser.set('online', true))
        .set('isCoPerformer', task.get('coPerformerId') === authUser.get('userId')),
    );
});

// Returns last active tasks of the authorized user.
export const getAuthUserLastTasks = createImmutableSelector(
  [getTasksWithSummary, getUserTasksIds, authUserSelector],
  (tasks, taskIds, authUser) => {
    return tasks
      .filter((task) => {
        const isCurrentUser =
          authUser &&
          (task.get('userId') === authUser.get('userId') || task.get('coPerformerId') === authUser.get('userId'));
        const isActive = task.get('status') === 'active';

        return isActive && isCurrentUser && taskIds.includes(task.get('taskId'));
      })
      .map((task) =>
        task
          .set('user', authUser.set('online', true))
          .set('isCoPerformer', task.get('coPerformerId') === authUser.get('userId')),
      )
      .slice(0, 10);
  },
);

// Returns task label groups with labels
export const getTaskLabelGroupsWithLabels = createImmutableSelector(
  [getTaskLabelGroups, getTaskLabels],
  (labelGroups, taskLabels) =>
    labelGroups.map((labelGroup) => {
      return labelGroup.set(
        'taskLabels',
        taskLabels.filter((label) => label.get('labelGroupId') === labelGroup.get('labelGroupId')),
      );
    }),
);

export const getTaskStatusesByIds = createSelector(
  [getTaskStatuses, (_, props) => props.statusIds],
  (taskStatuses, statusIds) => taskStatuses.filter((status) => statusIds.includes(status.get('statusId'))),
);

export const getTaskStatusByWorkflowId = createSelector(
  [getTaskStatuses, (_, props) => props.workflowId],
  (taskStatuses, workflowId) => {
    if (!workflowId) {
      return Immutable.List();
    }

    return taskStatuses
      .filter((taskStatus) => taskStatus.get('workflowId') === workflowId)
      .sort((a, b) => a.get('position') - b.get('position'));
  },
);

const prepareTask = (task, tasks) => {
  if (!task) {
    return null;
  }
  if (task.get('subTasks')) {
    task = task.set(
      'subTasks',
      task
        .get('subTasks')
        .map((id) => prepareTask(tasks.get(id), tasks))
        .filter((subTask) => subTask),
    );
  }

  return task;
};

// Return task with subtasks
export const getTaskWithSubTasks = createImmutableSelector([getTasks], (tasks) =>
  tasks.filter((task) => task.get('parentId') === null).map((task) => prepareTask(task, tasks)),
);
